// 文章详情页 ARTd1 / GVmbk(/article/:id;🔒 Authed,2026-06-13 owner 决策:匿名禁访文章详情)。
//   路由层 main.tsx 已包 authed();页内 requireLogin 防御保留(双保险)。feed 摘要(v_article_feed)仍匿名可读。
//   - v_article_public(useArticlePublic)取标题/封面/作者/分类/content
//   - ArticleSocialBar(fixed bottom,pen ExFCc):喜欢/不喜欢(VIP gate)+ 打赏 + 评论锚点
//   - ArticleComments(pen WsKjU):平铺评论列表 + 输入行(未登录跳 login;无 can_comment → VIP gate)
//   - TipModal(pen lNIg8):金币打赏作者底部弹层
//   - 头部 ellipsis moreBtn → ArticleActionsSheet:作者「编辑/删除」、非作者「举报」
// 契约源:docs/ui/user-page-flows/article.md ARTd1 + docs/api-list/user/articles.md
//        + 2026-06-12 spec(评论/打赏/社交底栏)。
// 设计稿:ui/dogdisk-user.pen Node ID GVmbk(页面-文章详情)+ ExFCc/WsKjU/lNIg8。

import { useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { BackButton } from '../components/back-button'
import type { JSONContent } from '@tiptap/core'
import { useCurrentUser, useToast, DomainCaptchaModal } from '@dogdisk/shared'
import { MobileScreen } from '../components/mobile-screen'
import { ArticleRender } from '../components/article-view/article-render'
import { ArticleSocialBar } from '../components/article-social-bar'
import { ArticleComments } from '../components/article-comments'
import { ArticleActionsSheet } from '../components/modals/article-actions-sheet'
import { DeleteConfirmDialog } from '../components/modals/delete-confirm-dialog'
import { ReportModal } from '../components/modals/report-modal'
import { TipModal } from '../components/modals/tip-modal'
import { useArticlePublic, useDeleteArticle } from '../_lib/article-api'
import { useMyVip, isVipUser } from '../_lib/vip-api'
import { useSocialStats, useToggleDislike, useToggleLike, type ArticleSocialStats } from '../_lib/social-api'
import { AdBanner } from '../components/ad/ad-banner'
import { useDomainCaptchaGate } from '../_lib/domain-captcha-gate'
import { resourceUrl } from '../_lib/resource-node-pref'

function fmtDate(ts: string | null): string {
  if (!ts) return ''
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** 估算阅读时长(分钟):遍历 content 文本字符数,按 ~400 字/分钟,最少 1 分钟。 */
function readingMinutes(content: JSONContent | null | undefined): number {
  if (!content) return 1
  let chars = 0
  const walk = (node: JSONContent) => {
    if (typeof node.text === 'string') chars += node.text.length
    for (const child of node.content ?? []) walk(child)
  }
  walk(content)
  return Math.max(1, Math.round(chars / 400))
}

export function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { status, user } = useCurrentUser()
  const toast = useToast()
  const [vipGate, setVipGate] = useState<'vote' | 'comment' | null>(null)
  const [tipOpen, setTipOpen] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const commentsRef = useRef<HTMLDivElement | null>(null)

  const articleQ = useArticlePublic(id)
  const article = articleQ.data
  const vipQuery = useMyVip(status === 'authenticated')
  const isVip = isVipUser(vipQuery.data)
  const canComment = vipQuery.data?.can_comment === true
  // "每日一次"判定 = 有任意 active 订阅段（VIP user / forum_creator / dating_creator 都算）。
  // owner 2026-06-30：只要有 VIP/创作者订阅就每天一次；故用 active_segments 而非 isVipUser。
  const hasActiveSubscription = (vipQuery.data?.active_segments?.length ?? 0) > 0
  const domGate = useDomainCaptchaGate({ isSubscriber: hasActiveSubscription })
  const [gatePassedId, setGatePassedId] = useState<string | null>(null)
  // gate 生效条件：文章已加载 + 需要 gate + 内容就绪 + 本文章尚未通过（换 id 自动重新生效）
  const domCaptchaActive = !!article && domGate.shouldGate && domGate.ready && gatePassedId !== id
  const socialStats = useSocialStats('article', id ?? null)
  const artStats = (socialStats.data ?? null) as ArticleSocialStats | null
  const likeMut = useToggleLike('article', id ?? '')
  const dislikeMut = useToggleDislike('article', id ?? '')
  const deleteMut = useDeleteArticle()

  const isAuthor = status === 'authenticated' && !!article && article.user_id === user?.user_id
  const readMins = useMemo(() => readingMinutes(article?.content), [article?.content])

  const requireLogin = (): boolean => {
    if (status === 'authenticated') return false
    navigate(`/auth/login?next=${encodeURIComponent(`#/article/${id}`)}`)
    return true
  }

  const onConfirmDelete = async () => {
    if (!id) return
    await deleteMut.mutateAsync(id)
    setConfirmDelete(false)
    setSheetOpen(false)
    navigate('/me/articles')
  }

  return (
    <MobileScreen bg="white">
      {/* Header(wM8tN):backBtn / 居中静态「文章」/ ellipsis moreBtn */}
      <header
        data-testid="ARTd1"
        className="flex items-center justify-between gap-3 px-4 pt-[max(10px,env(safe-area-inset-top))] pb-2"
      >
        <BackButton className="shrink-0" />
        <h1 className="flex-1 truncate text-center text-[16px] font-semibold text-[#1A1A1A]">文章</h1>
        {article ? (
          <button
            type="button"
            data-testid="ARTd1-more"
            onClick={() => setSheetOpen(true)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#F6F7F8] text-[#1A1A1A] active:bg-[#EDEEF0]"
            aria-label="更多"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>
          </button>
        ) : (
          <span className="h-9 w-9 shrink-0" aria-hidden="true" />
        )}
      </header>

      <div className="flex-1 overflow-y-auto pb-24">
        {articleQ.isLoading ? (
          <div className="flex flex-col gap-3 px-5 pt-8">
            <div className="h-40 w-full animate-pulse rounded-[14px] bg-surface-200" />
            <div className="h-6 w-2/3 animate-pulse rounded bg-surface-200" />
          </div>
        ) : !article ? (
          <div className="flex flex-col items-center gap-2 pt-20 text-[#9CA3AF]">
            <p className="text-[15px]">文章不存在或已下架</p>
            <button type="button" onClick={() => navigate('/')} className="mt-2 rounded-full bg-[#19caad] px-6 py-2 text-white">回首页</button>
          </div>
        ) : (
          <article>
            {/* Hero(RzhL5):封面 + H1 + metaRow(作者 + 阅读时长 + 分类 chip 靠右) */}
            <div className="flex flex-col gap-3 px-5 pb-4 pt-2">
              {article.cover_thumb_url && (
                <img
                  src={resourceUrl(article.cover_thumb_url)}
                  alt=""
                  className="w-full h-auto rounded-[14px]"
                />
              )}
              <h2
                className="text-[24px] font-bold leading-[1.3] text-[#1A1A1A]"
                style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}
              >
                {article.title}
              </h2>

              <div className="flex items-center gap-2.5">
                <Link to={`/u/${article.user_id}`} data-testid="ARTd1-author" className="flex items-center gap-2.5 no-underline">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#C9E4DE] text-[11px] text-[#1A1A1A]">
                    {article.owner_avatar ? (
                      <img src={resourceUrl(article.owner_avatar)} alt="" className="h-full w-full object-cover" />
                    ) : (
                      (article.owner_nickname ?? '?').slice(0, 1)
                    )}
                  </span>
                  <span className="flex flex-col gap-0.5">
                    <span className="text-[13px] font-semibold text-[#1A1A1A]">{article.owner_nickname}</span>
                    <span className="text-[11px] text-[#9CA3AF]">
                      {[fmtDate(article.published_at), `${readMins} 分钟阅读`].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                </Link>
                <span className="flex-1" />
                {article.category_name && article.category_id && (
                  <Link
                    to={`/?category_id=${article.category_id}`}
                    data-testid="ARTd1-category"
                    className="shrink-0 rounded-full border border-[#19caad] bg-[#F0FBF9] px-2.5 py-1 text-[12px] text-[#19caad] no-underline"
                  >
                    {article.category_name}
                  </Link>
                )}
              </div>
            </div>

            {/* Top Banner Ads(Z6SKZI)— Hero 之后、正文之前,5 条横幅广告 */}
            <div className="px-5">
              <AdBanner location="top" />
            </div>

            {/* Body(nsQCX):正文 + 嵌入卡片 */}
            <div className="px-5">
              <ArticleRender content={article.content} />
            </div>

            {/* Bottom Banner Ads(q5s85)— 正文之后、评论之前,5 条横幅广告 */}
            <div className="px-5">
              <AdBanner location="bottom" />
            </div>

            {/* 评论区(WsKjU):平铺列表 + 输入行(gate:未登录跳 login / 无评论权益弹 VIP 层) */}
            <ArticleComments
              ref={commentsRef}
              articleId={id!}
              canComment={canComment}
              isLoggedIn={status === 'authenticated'}
              onRequireLogin={() => void requireLogin()}
              onRequireVip={() => setVipGate('comment')}
            />
          </article>
        )}
      </div>

      {/* 社交底栏(ExFCc,fixed bottom):喜欢/不喜欢(VIP gate)+ 打赏 + 评论锚点 */}
      {article && (
        <ArticleSocialBar
          stats={artStats}
          isPending={likeMut.isPending || dislikeMut.isPending}
          onLike={() => {
            if (requireLogin()) return
            if (!isVip) { setVipGate('vote'); return }
            void likeMut.mutateAsync()
          }}
          onDislike={() => {
            if (requireLogin()) return
            if (!isVip) { setVipGate('vote'); return }
            void dislikeMut.mutateAsync()
          }}
          onReward={() => {
            if (requireLogin()) return
            setTipOpen(true)
          }}
          onComment={() => commentsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        />
      )}

      {/* moreBtn → 操作菜单(作者编辑/删除;非作者举报) */}
      {article && (
        <ArticleActionsSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          isAuthor={isAuthor}
          onEdit={() => {
            setSheetOpen(false)
            navigate(`/article/${id}/edit`)
          }}
          onDelete={() => setConfirmDelete(true)}
          onReport={() => {
            setSheetOpen(false)
            setReportOpen(true)
          }}
        />
      )}

      {/* 删除二次确认(mdYih) */}
      <DeleteConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => void onConfirmDelete()}
        title="确认删除这篇文章?"
        subtitle="删除后文章将下架,不再对外展示。"
        loading={deleteMut.isPending}
      />

      {/* 举报(DY9nl):target = 文章作者 */}
      {article && (
        <ReportModal
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          targetUserId={article.user_id}
          targetUsername={article.owner_username ?? undefined}
        />
      )}

      {/* VIP gate 弹层:vote = 仅 VIP 可投票;comment = 套餐不含评论权益 */}
      {vipGate !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6" onClick={() => setVipGate(null)}>
          <div className="w-full max-w-[320px] rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
            <p className="text-center text-[16px] font-semibold text-[#1A1A1A]">
              {vipGate === 'vote' ? '仅 VIP 可投票' : '开通评论权益'}
            </p>
            <p className="mt-2 text-center text-[12px] text-[#9CA3AF]">
              {vipGate === 'vote' ? '升级 VIP 解锁喜欢 / 不喜欢功能' : '当前套餐不含评论权益,升级后即可评论'}
            </p>
            <button type="button" className="mt-4 h-11 w-full rounded-lg bg-[#19caad] text-[14px] font-semibold text-white" onClick={() => { setVipGate(null); navigate('/vip') }}>立即升级</button>
            <button type="button" className="mt-2 h-11 w-full rounded-lg bg-[#F6F7F8] text-[14px] font-medium text-[#1A1A1A]" onClick={() => setVipGate(null)}>取消</button>
          </div>
        </div>
      )}

      {/* 打赏弹窗(lNIg8):条件挂载 = 关闭时卸载,内部 state 自动重置;invalidate 已由 useTipArticle onSuccess 内做,这里只补成功 toast */}
      {article && tipOpen && (
        <TipModal
          open={true}
          articleId={id!}
          authorName={article.owner_nickname ?? '匿名用户'}
          onClose={() => setTipOpen(false)}
          onSuccess={() => toast.push('打赏成功,感谢支持', { variant: 'success' })}
        />
      )}

      {/* 域名验证码 gate:普通用户 1 分钟一次 / 订阅用户(VIP/创作者)每天一次;未过盖住正文,取消返回上一页 */}
      {domCaptchaActive && (
        <DomainCaptchaModal
          open
          content={domGate.content}
          segments={domGate.segments}
          onPass={() => {
            domGate.markPassed()
            if (id) setGatePassedId(id)
          }}
          onCancel={() => navigate(-1)}
          onBuyVip={() => navigate('/vip/buy')}
        />
      )}
    </MobileScreen>
  )
}
